import { ChangeDetectionStrategy, Component, OnInit, Signal, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideCircleAlert, lucideHouse } from '@ng-icons/lucide';

import { FocusOnlyRadioGroupDirective } from 'app/home/common/directives/focus-only-radio-group.directive';
import { ReadonlyRadioDirective } from 'app/home/common/directives/readonly-radio.directive';
import { BaseQuoteForm } from 'app/home/create-quote/form-config/quote-form-base';
import { QuoteFormContextDecorator } from 'app/home/create-quote/form-config/quote-form-decorator';
import { Lockable, LockedQuoteAlertComponent, WithLockableFields } from 'app/home/create-quote/form-locking';
import { FormCardComponent } from 'app/home/create-quote/forms/shared/form-card.component';
import { UnitNumberCheckQuoteFormInput } from 'app/home/create-quote/forms/unit-number-check/unit-number-check.types';
import { QuoteDraftState } from 'app/home/global-store/quote-draft/quote-draft.model';
import { FeatureFlagSignalsService } from 'app/shared/services/splitIO/feature-flag.service';

@Component({
  selector: 'app-unit-number-check',
  imports: [ReactiveFormsModule, NgIconComponent, FormCardComponent, ReadonlyRadioDirective, LockedQuoteAlertComponent, FocusOnlyRadioGroupDirective],
  templateUrl: './unit-number-check.component.html',
  providers: [provideIcons({ lucideHouse, lucideCircleAlert })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
})
@QuoteFormContextDecorator({
  legacyShape: '/quote/unit_number_check',
  isAutoAdvanced: true,
})
export class QuoteFormUnitNumberCheckComponent extends BaseQuoteForm<UnitNumberCheckQuoteFormInput> implements OnInit, Lockable {
  private featureFlagSignalsService = inject(FeatureFlagSignalsService);
  public isAutoAdvanceEnabled = this.featureFlagSignalsService.isAutoAdvanceEnabled;

  public formDataIn: UnitNumberCheckQuoteFormInput = this.quoteDraftStore.unitNumberCheckQuoteFormSelector();

  public formDefinition = {
    hasUnitNumber: new FormControl<boolean | undefined>(undefined, Validators.required),
  };
  public unitNumberCheckForm = new FormGroup(this.formDefinition);

  public override autoAdvanceControlRef = this.unitNumberCheckForm.controls.hasUnitNumber;

  public get showFormRequiredError(): boolean {
    if (this.isAutoAdvanceEnabled()) {
      return false;
    }
    const control = this.unitNumberCheckForm.get('hasUnitNumber');
    return !!control?.invalid && !!control?.touched;
  }

  public ngOnInit() {
    if (this.formDataIn && this.isFieldLocked('hasUnitNumber')) {
      this.unitNumberCheckForm.controls.hasUnitNumber.setValue(this.formDataIn.hasUnitNumber);
    }
  }

  public isLocked: Signal<{ [key: string]: boolean }> = WithLockableFields.createIsLocked.call(this);
  public isFieldLocked = WithLockableFields.isFieldLocked;
  public allFieldsLocked = WithLockableFields.allFieldsLocked;

  public getFormControls() {
    return this.unitNumberCheckForm.controls;
  }

  private getFormValues(): Pick<QuoteDraftState, 'hasUnitNumber'> {
    const { controls } = this.unitNumberCheckForm;
    const formValues = {
      hasUnitNumber: controls.hasUnitNumber.value ?? undefined,
    };

    return formValues;
  }

  public handleSubmit(e: Event) {
    e.preventDefault();
    this.unitNumberCheckForm.markAllAsTouched();
    if (this.unitNumberCheckForm.valid) {
      if (this.allFieldsLocked()) {
        super.navigateForward();
      } else {
        super.saveFormData(this.getFormValues());
      }
    }
  }
}
